import { useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoaderCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useLangPrefix } from "../hooks/useLangPrefix";
import LanguageSwitcher from "../components/layout/LanguageSwitcher";
import PasswordInput from "../components/common/PasswordInput";
import api from "../api/client";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const { prefix } = useLangPrefix();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(
    () => searchParams.get("token")?.trim() ?? "",
    [searchParams],
  );

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const hasToken = Boolean(token);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!hasToken) {
      setError(t("resetPassword.missingToken"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("resetPassword.passwordsMismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/manager/auth/reset-password", {
        token,
        new_password: password,
      });
      toast.success(t("resetPassword.successToast"));
      navigate(`${prefix}/login`);
    } catch (err) {
      const status = err?.response?.status
      if (!status || status >= 500) {
        Sentry.captureException(err, {
          tags: { module: 'auth', action: 'reset_password' },
        })
      }
      const detail = err.response?.data?.detail;
      let msg = t("resetPassword.genericError");
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) {
        const parts = detail.map((d) =>
          d && typeof d === "object" && "msg" in d ? d.msg : String(d),
        );
        if (parts.length) msg = parts.join(" ");
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-zinc-100 p-4 text-zinc-900 scheme-light">
      <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
        <LanguageSwitcher />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-100/70 via-transparent to-cyan-100/50" />

      <div className="w-full max-w-sm relative">
        <div className="text-center mb-8">
          <div className="inline-flex items-center">
            <img
              src="/full_logo.png"
              alt="Growchat"
              className="w-40 h-auto rounded-md object-cover"
            />
          </div>
          <p className="text-sm text-gray-500">{t("login.tagline")}</p>
        </div>

        <div className="card p-6">
          <h2 className="mb-1 text-lg font-semibold text-zinc-900">
            {t("resetPassword.title")}
          </h2>
          <p className="mb-5 text-sm text-gray-600">
            {t("resetPassword.subtitle")}
          </p>

          {!hasToken && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t("resetPassword.missingToken")}
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-zinc-600">
                {t("resetPassword.newPassword")}
              </label>
              <PasswordInput
                className="input"
                placeholder={t("resetPassword.passwordPlaceholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                disabled={!hasToken}
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-zinc-600">
                {t("resetPassword.confirmPassword")}
              </label>
              <PasswordInput
                className="input"
                placeholder={t("resetPassword.passwordPlaceholder")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                disabled={!hasToken}
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !hasToken}
              className="btn-primary w-full justify-center py-2.5 mt-1 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Spinner /> {t("resetPassword.submitting")}
                </span>
              ) : (
                t("resetPassword.submit")
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-500 mt-5">
            <Link
              to={`${prefix}/login`}
              className="font-medium text-indigo-600 hover:text-indigo-700"
            >
              {t("resetPassword.backToLogin")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return <LoaderCircle className="h-4 w-4 animate-spin" />;
}
